import { v4 } from "uuid";
import type { Event, IMessage } from "@/types";
import { buildAdkUserParts, normalizeAdkParts } from "./parts";
import type {
  AdkProtocolOptions,
  BuildAdkUserPartsInput,
  MessageEventMeta,
  NormalizedAdkPart,
} from "./types";

const defaultProtocolOptions: Required<AdkProtocolOptions> = {
  hideAsyncTaskEvents: true,
  pairFunctionResponses: true,
};

/**
 * 获取消息对应的服务端 eventId。
 * session events 补偿和去重会优先用服务端 id，避免前端临时 id 导致重复插入。
 */
export const getAdkMessageEventId = (message: IMessage) =>
  message.eventId || message.raw?.id || message.id;

/**
 * 获取单次模型调用 id。流式分片与 sessionDetails 完整消息通常共享 invocationId，
 * 可用它判断哪些文本属于同一轮回复。
 */
export const getAdkMessageInvocationId = (message: IMessage) =>
  message.invocationId || message.raw?.invocationId;

/**
 * 后端为异步任务补链路时可能插入 user task 事件，这类事件用于关联任务，
 * 默认不作为用户可见消息展示。
 */
export const isAdkAsyncTaskEvent = (event: Event) =>
  event.author === "user" &&
  event.content?.parts?.some((part) => Boolean((part as any)?.task));

export const isUserFunctionResponseEvent = (event: Event) =>
  event.content?.role === "user" &&
  Array.isArray(event.content.parts) &&
  event.content.parts.length > 0 &&
  event.content.parts.every((part) => Boolean(part?.functionResponse));

/**
 * 提取 event 级元信息，保证 stream 最后一条里的 modelCode / usageMetadata
 * 能透传到 IMessage，业务侧可据此展示 token 消耗等信息。
 */
export const getAdkEventMeta = (event: Event): MessageEventMeta => ({
  eventId: event.id,
  invocationId: event.invocationId,
  sessionId: event.sessionId,
  timestamp: event.timestamp,
  author: event.author,
  modelCode: event.modelCode,
  usageMetadata: event.usageMetadata,
  finishReason: event.finishReason,
  isLike: event.isLike,
});

export const createAdkMessage = (
  event: Event,
  part: NormalizedAdkPart,
  role: IMessage["role"] = "bot",
): IMessage => ({
  id: v4(),
  role,
  ...getAdkEventMeta(event),
  thought: part.thought,
  functionCall: part.functionCall,
  functionResponse: part.functionResponse,
  fileData: part.fileDataList || (part.fileData ? [part.fileData] : undefined),
  inlineData: part.inlineData,
  executableCode: part.executableCode,
  codeExecutionResult: part.codeExecutionResult,
  text: part.errorMessage || part.text,
  renderedContent: event.groundingMetadata?.searchEntryPoint?.renderedContent,
  raw: event,
});

/**
 * 将单个 ADK event 转换为 SDK 消息。
 *
 * followup 事件由上层作为 suggestedQuestions 处理；user functionResponse
 * 默认只用于回填对应 functionCall，不单独展示成一条用户消息。
 */
export const mapAdkEventToMessages = (
  event: Event,
  options?: AdkProtocolOptions,
): IMessage[] => {
  const mergedOptions = { ...defaultProtocolOptions, ...options };
  if (mergedOptions.hideAsyncTaskEvents && isAdkAsyncTaskEvent(event)) return [];
  if (mergedOptions.pairFunctionResponses && isUserFunctionResponseEvent(event))
    return [];
  if (event.content?.role === "followup") return [];

  if (event.content?.parts?.length) {
    const role = event.content.role === "user" ? "user" : "bot";
    return normalizeAdkParts(event.content.parts).map((part) =>
      createAdkMessage(event, part, role),
    );
  }

  if (event.errorMessage) {
    return [
      {
        id: v4(),
        role: "bot",
        text: event.errorMessage,
        ...getAdkEventMeta(event),
        raw: event,
      },
    ];
  }

  if (event.actions?.artifactDelta) {
    return [
      {
        id: v4(),
        role: "bot",
        ...getAdkEventMeta(event),
        raw: event,
      },
    ];
  }

  return [];
};

export const mapAdkSessionEventsToMessages = (
  events: Event[] = [],
  options?: AdkProtocolOptions,
): IMessage[] => pairFunctionResponses(events.flatMap((event) =>
  mapAdkEventToMessages(event, options),
));

/**
 * 消息去重分两层：
 * - 先按前端 id 去重。
 * - 再按 invocationId + thought + fileUri 聚合同一轮 bot 文本，保留更完整的长文本。
 *
 * 第二层主要解决 SSE 增量文本和 sessionDetails 完整文本同时存在时重复展示的问题。
 */
export const dedupeAdkMessages = (messages: IMessage[]): IMessage[] => {
  const seenMessageIds = new Set<string>();
  let hasDuplicate = false;

  const uniqueIdMessages = messages.filter((message) => {
    if (seenMessageIds.has(message.id)) {
      hasDuplicate = true;
      return false;
    }
    seenMessageIds.add(message.id);
    return true;
  });

  const botTextGroups = new Map<
    string,
    Array<{ message: IMessage; normalizedText: string }>
  >();

  uniqueIdMessages.forEach((message) => {
    const invocationId = getAdkMessageInvocationId(message);
    if (
      message.role !== "bot" ||
      !invocationId ||
      typeof message.text !== "string"
    )
      return;

    const normalizedText = message.text.replace(/\s+/g, " ").trim();
    if (!normalizedText) return;

    const fileKey = (message.fileData || [])
      .map((file) => file.fileUri)
      .join("|");
    const groupKey = `${invocationId}\u0000${Boolean(
      message.thought,
    )}\u0000${fileKey}`;
    const group = botTextGroups.get(groupKey) || [];
    group.push({ message, normalizedText });
    botTextGroups.set(groupKey, group);
  });

  const redundantIds = new Set<string>();
  botTextGroups.forEach((group) => {
    const completeTexts: string[] = [];
    [...group]
      .sort((a, b) => b.normalizedText.length - a.normalizedText.length)
      .forEach(({ message, normalizedText }) => {
        if (
          completeTexts.some((completeText) =>
            completeText.includes(normalizedText),
          )
        ) {
          redundantIds.add(message.id);
          hasDuplicate = true;
          return;
        }
        completeTexts.push(normalizedText);
      });
  });

  return hasDuplicate
    ? uniqueIdMessages.filter((message) => !redundantIds.has(message.id))
    : messages;
};

export const insertMissingAdkSessionEvents = (
  currentMessages: IMessage[],
  sessionEvents: Event[],
  options?: AdkProtocolOptions & { removedInvocationIds?: Set<string> },
): IMessage[] => {
  // 先用 sessionEvents 里的 functionResponse 回填已有 functionCall，避免工具状态丢失。
  let nextMessages = pairFunctionResponses(currentMessages);
  const functionResponses = collectFunctionResponses(sessionEvents);
  if (functionResponses.size) {
    nextMessages = pairFunctionResponses(nextMessages, functionResponses);
  }

  const renderedEventIds = new Set(
    nextMessages.map(getAdkMessageEventId).filter(Boolean),
  );

  const missingMessages = sessionEvents
    .filter((event) => {
      if (options?.removedInvocationIds?.has(event.invocationId || ""))
        return false;
      const parts = event.content?.parts;
      return (
        Boolean(event.id) &&
        event.content?.role === "model" &&
        Array.isArray(parts) &&
        parts.length > 0 &&
        !renderedEventIds.has(event.id)
      );
    })
    .flatMap((event) => mapAdkEventToMessages(event, options));

  if (!missingMessages.length) return nextMessages;

  // 按服务端事件顺序插入缺失消息，尽量保持历史消息和流式消息的时间线一致。
  const eventOrder = new Map(
    sessionEvents.map((event, index) => [event.id, index]),
  );
  const getMessageOrder = (message: IMessage) => {
    const eventId = getAdkMessageEventId(message);
    if (eventId && eventOrder.has(eventId)) return eventOrder.get(eventId)!;

    const timestamp = message.timestamp ?? message.raw?.timestamp;
    if (timestamp !== undefined) {
      const nextEventIndex = sessionEvents.findIndex(
        (event) => (event.timestamp || 0) > timestamp,
      );
      return nextEventIndex < 0
        ? sessionEvents.length + 0.5
        : nextEventIndex - 0.5;
    }
    return Number.POSITIVE_INFINITY;
  };

  missingMessages.forEach((message) => {
    const targetOrder = getMessageOrder(message);
    let insertIndex = nextMessages.findIndex((current) => {
      const currentOrder = getMessageOrder(current);
      return Number.isFinite(currentOrder) && currentOrder > targetOrder;
    });
    if (insertIndex < 0) insertIndex = nextMessages.length;
    nextMessages.splice(insertIndex, 0, message);
  });

  return dedupeAdkMessages(pairFunctionResponses(nextMessages));
};

/**
 * 从 session events 中收集工具响应，用于回填前面已经渲染出来的 functionCall。
 */
export const collectFunctionResponses = (events: Event[]) => {
  const functionResponses = new Map<
    string,
    NonNullable<IMessage["functionResponse"]>
  >();
  events.forEach((event) => {
    event.content?.parts?.forEach((part) => {
      if (part.functionResponse?.id) {
        functionResponses.set(part.functionResponse.id, part.functionResponse);
      }
    });
  });
  return functionResponses;
};

export const pairFunctionResponses = (
  messages: IMessage[],
  responses?: Map<string, NonNullable<IMessage["functionResponse"]>>,
): IMessage[] => {
  const responseMap =
    responses ||
    new Map(
      messages
        .filter((message) => message.functionResponse?.id)
        .map((message) => [
          message.functionResponse!.id!,
          message.functionResponse!,
        ]),
    );

  if (!responseMap.size) return messages;

  return messages.map((message) => {
    const callId = message.functionCall?.id;
    if (!callId || message.functionResponse || !responseMap.has(callId)) {
      return message;
    }
    return {
      ...message,
      functionResponse: responseMap.get(callId),
    };
  });
};

/**
 * 构造本地乐观用户消息。
 * controller 在真正发请求前会先插入这些消息，让 UI 立即响应用户输入。
 */
export const buildAdkUserMessage = ({
  text = "",
  files = [],
  functionResponse,
}: BuildAdkUserPartsInput): IMessage[] => {
  const timestamp = Date.now();
  const messages: IMessage[] = [];

  if (files.length) {
    messages.push({
      id: v4(),
      role: "user",
      timestamp,
      fileData: files
        .map((file) => {
          const fileUri =
            file.tempUrl ||
            file.fileUrl ||
            file.response?.fileUrl ||
            file.response?.tempUrl;
          if (!fileUri) return null;
          return {
            displayName:
              file.fileName || file.name || file.response?.fileName || "",
            mimeType: file.mimeType || file.response?.mimeType || "",
            fileUri,
          };
        })
        .filter(Boolean) as IMessage["fileData"],
    });
  }

  if (text.trim()) {
    messages.push({ id: v4(), role: "user", text, timestamp });
  }

  if (functionResponse) {
    messages.push({ id: v4(), role: "user", functionResponse, timestamp });
  }

  return messages;
};

export { buildAdkUserParts };
